import axios, { AxiosInstance } from 'axios';
import { User } from 'oidc-client-ts';

// Create an Axios instance for Sentinel API
const sentinelNetwork: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_APP_API_BASE_URL,
  headers: {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  }
});

/**
 * Retrieve the current OIDC user from sessionStorage.
 * oidc-client-ts stores it under `oidc.user:<authority>:<client_id>`.
 */
function getOidcUser(): User | null {
  const authority = import.meta.env.VITE_OIDC_AUTHORITY || 'https://sso.mercedes-benz.com';
  const clientId = import.meta.env.VITE_OIDC_CLIENT_ID || '';
  const key = `oidc.user:${authority}:${clientId}`;
  const raw = sessionStorage.getItem(key);
  if (raw) {
    try { return User.fromStorageString(raw); } catch { /* fall through */ }
  }
  // Fallback: scan all oidc.user: keys (handles trailing-slash authority variants)
  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (k?.startsWith('oidc.user:') && k !== key) {
      const v = sessionStorage.getItem(k);
      if (v) {
        try { return User.fromStorageString(v); } catch { /* continue */ }
      }
    }
  }
  return null;
}

/**
 * Request interceptor: attach the current OIDC Bearer token to every request.
 *
 * Intentionally simple — no pre-flight renewal logic. If the token is
 * expired, the request will fail with a 401/403 from API Gateway. App.tsx's
 * initializeAuth() catches that and triggers a full re-login via
 * handleStaleSession(). Keeping renewal out of the interceptor avoids the
 * signinSilent → timeout → clearOidcSession → reload loop that was preventing
 * authenticated requests from ever reaching the backend.
 */
sentinelNetwork.interceptors.request.use(
  (config) => {
    const user = getOidcUser();
    if (user?.access_token) {
      config.headers.Authorization = `Bearer ${user.access_token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: log server errors, propagate all others to callers.
sentinelNetwork.interceptors.response.use(
  (response) => {
    if (response?.data?.status === 500) {
      console.error(response?.data?.message || 'Internal Server Error');
    }
    return response;
  },
  (error) => Promise.reject(error)
);

export default sentinelNetwork;
